import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { message, Space, Typography } from 'antd';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import client from '../../api/api';
import Loading from '../../libs/loading';
import { renderError40x } from '../../libs/error/render-error';

const media = {
  tablet: '@media(min-width: 768px)',
  desktop: '@media(min-width: 1366px)',
};

const SettlementAdvanceWrap = styled.div`
  width: 90%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2,
  h3,
  h4 {
    color: white;
  }

  h2 {
    display: inline-flex;
    font-size: 2pc;
    margin-bottom: 2pc;
  }

  h4 {
    margin: 0;
  }

  h5 {
    color: #ffbb0f;
    font-size: 14px;
    font-weight: bold;
  }

  p {
    color: white;
    margin-bottom: 25px;
  }

  button {
    border-radius: 10px;
    border: none;
    padding: 6px;
    font-size: 18px;
    margin: 10px;
  }

  button.confirm {
    background: #ffbb0f;
  }

  button.reject {
    background: transparent;
    color: #ffbb0f;
    border: 1px solid #ffbb0f;
  }

  ${media.tablet} {
    button {
      width: 18pc;
    }
  }

  ${media.desktop} {
    width: 90%;
    margin: 0 auto;
  }
`;

const Separator = styled.div`
  background: #ffbb0f;
  width: 6pc;
  height: 6px;
  margin-bottom: 4pc;

  ${media.desktop} {
    width: 12pc;
    margin-bottom: 1pc;
  }
`;

const Content = styled.div`
  ${media.tablet} {
    padding: 30px;
  }
`;

const ResultsWrap = styled.div`
  width: 100%;
  margin: 0 auto;

  p {
    width: 100%;
    font-size: 25px;
    text-align: center;
  }

  p.success {
    color: #19c482;
  }

  p.rejected {
    color: rgb(224, 63, 63);
  }

  ${media.tablet} {
    display: flex;
    justify-content: column-reverse;
    flex-direction: row-reverse;
  }
`;

const ButtonsWrap = styled.div`
  width: 60%;
  margin: 0 auto;

  ${media.tablet} {
    display: flex;
    justify-content: column-reverse;
    flex-direction: row-reverse;
  }
`;

const Footer = styled.div`
  display: flex;
  width: 100%;
  height: 40px;
  justify-content: center;

  a {
    margin-top: 20px;
  }
`;

const { Title, Text } = Typography;

const browserLocale = navigator.language || 'es-AR';

const numberFormatter = new Intl.NumberFormat(browserLocale, {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const SettlementAdvance = ({ billingId }) => {
  const [advanceId, setAdvanceId] = useState('');
  const [originalAmount, setOriginalAmmout] = useState(0);
  const [cost, setCost] = useState(0);
  const [total, setTotal] = useState(0);
  const [success, setSuccess] = useState(false);
  const [advanceRejected, setAdvanceRejected] = useState(false);
  const [authorizationShortToken, setAuthorizationShortToken] = useState('');
  const [isCreatingAdvance, setIsCreatingAdvance] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleError = (error, msgError) => {
    console.error(error);
    if (error.response?.status === 400) {
      renderError40x(msgError, error.response.data);
    } else {
      message.error(msgError);
    }
  };

  const createAdvance = useCallback(async () => {
    try {
      setIsCreatingAdvance(true);

      const { data } = await client.endpoints.carrier.createSettlementAdvance(billingId);

      setAdvanceId(data.id);
      setAuthorizationShortToken(data.shortToken);

      setOriginalAmmout(data.final);
      setCost(data.cost);
      setTotal(data.totalAdvance);
    } catch (error) {
      handleError(error, 'Error al crear el adelanto de pago');
    } finally {
      setIsCreatingAdvance(false);
    }
  }, [billingId]);

  useEffect(() => {
    if (billingId) {
      createAdvance();
    }
  }, [billingId, createAdvance]);

  const acceptAdvance = async () => {
    try {
      setIsExecuting(true);

      await client.endpoints.carrier.acceptSettlementAdvance(advanceId, authorizationShortToken);

      setSuccess(true);
    } catch (error) {
      handleError(error, 'Error al aceptar el adelanto de pago');
    } finally {
      setIsExecuting(false);
    }
  };

  const rejectAdvance = async () => {
    try {
      setIsExecuting(true);

      await client.endpoints.carrier.rejectSettlementAdvance(advanceId);

      setAdvanceRejected(true);
    } catch (error) {
      handleError(error, 'Error al rechazar el adelanto de pago');
    } finally {
      setIsExecuting(false);
    }
  };

  if (isCreatingAdvance) {
    return (
      <>
        <SettlementAdvanceWrap>
          <Title level={3}>Adelanto de pago</Title>
          <Separator />
          <Content>
            <Loading />
          </Content>
        </SettlementAdvanceWrap>
      </>
    );
  }

  return (
    <>
      {isExecuting && <Loading />}
      <SettlementAdvanceWrap>
        <Title level={3}>Adelanto de pago</Title>
        <Separator />
        <Content>
          <Space direction="vertical" size="large">
            <Space direction="vertical" size={2}>
              <Text>Monto a adelantar</Text>
              <Title level={4}>$ {numberFormatter.format(originalAmount)}</Title>
            </Space>

            <Space direction="vertical" size={2}>
              <Text>Costo del adelanto</Text>
              <Space align="baseline">
                <Title level={4}>$ {numberFormatter.format(cost)}</Title>
                <Text style={{ color: 'white' }}>+ IVA</Text>
              </Space>
            </Space>

            <Space direction="vertical" size={2}>
              <Text>Monto a depositar</Text>
              <Title level={4}>$ {numberFormatter.format(total)}</Title>
            </Space>

            <Title level={5}>
              El monto a depositar puede demorar hasta 5 días hábiles dependiendo el momento de la
              solicitud.
            </Title>
          </Space>
        </Content>
        <ResultsWrap>
          {advanceRejected ? (
            <p className="rejected">Si cambiás de opinión el adelanto va a seguir disponible.</p>
          ) : (
            ''
          )}
          {success ? <p className="success">Tu solicitud de adelanto fue aprobada</p> : ''}
        </ResultsWrap>
        <ButtonsWrap>
          {advanceId && !advanceRejected && !success ? (
            <>
              <button
                type="button"
                className="confirm"
                onClick={() => {
                  acceptAdvance();
                }}
                disabled={isExecuting}
              >
                Confirmar
              </button>
              <button
                type="button"
                className="reject"
                onClick={() => {
                  rejectAdvance();
                }}
                disabled={isExecuting}
              >
                Rechazar
              </button>
            </>
          ) : (
            ''
          )}
        </ButtonsWrap>
      </SettlementAdvanceWrap>
      <Footer>
        <Link to="/settlementAdvance/terms" target="_blank" className="link">
          Términos y condiciones
        </Link>
      </Footer>
    </>
  );
};

SettlementAdvance.propTypes = {
  billingId: PropTypes.string.isRequired,
};

export default SettlementAdvance;
